import React, { useState, useEffect, useCallback } from "react";
import { NavLink, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../Config";
import TransportMenu from "./TransportMenu";
import Spinner from "react-bootstrap/Spinner";
import Form from "react-bootstrap/Form";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import { scrollToTop } from "../Shared/ScrollOnTop";

/*
 * Add or edit one vehicle. The same component serves both routes: with an :id in the URL
 * it loads and updates, without one it creates.
 *
 * When editing, the routes this bus serves are listed read-only. They are changed on the
 * Bus Routes screen, because a route carries the fee that students are billed and must
 * not be editable from a fleet screen by accident.
 */
function EditVehicle() {
  const params = useParams();
  const navigate = useNavigate();
  const vehicleId = parseInt(params.id, 10) || 0;
  const isEdit = vehicleId > 0;

  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const UserName = localStorage.getItem("UserName");
  const MemberType = localStorage.getItem("MemberType");

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [validated, setValidated] = useState(false);
  const [assignments, setAssignments] = useState([]);

  const [v, setV] = useState({
    Vehicle_ID: 0,
    Registration_No: "",
    Model: "",
    Capacity: "",
    FuelType: "Diesel",
    Ownership: "Private",
    Engine_No: "",
    Chassis_No: "",
    Area_Served: "",
    Status: "Active",
    Remarks: "",
    Is_Active: true,
  });

  const auth = useCallback(
    (extra) => ({
      AccessKey,
      UserID,
      UserName,
      MemberType: MemberType || "Admin",
      SchoolID: 1,
      ...extra,
    }),
    [AccessKey, UserID, UserName, MemberType]
  );

  const post = useCallback(
    (url, data) =>
      axios({
        method: "post",
        url: config.base_url + url,
        headers: { "Content-Type": "application/json" },
        data,
      }),
    []
  );

  useEffect(() => {
    document.title = isEdit ? "Edit Vehicle" : "Add Vehicle";
    scrollToTop();
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit) return;

    setLoading(true);
    Promise.all([
      post("Vehicle/GetVehicleByID", auth({ ID: vehicleId })),
      post("Vehicle/GetVehicleAssignments", auth({ ID: vehicleId })),
    ])
      .then(([vehicleRes, assignRes]) => {
        if (vehicleRes.data.status_code === 1 && vehicleRes.data.Vehicle) {
          const d = vehicleRes.data.Vehicle;
          setV({
            Vehicle_ID: d.Vehicle_ID,
            Registration_No: d.Registration_No || "",
            Model: d.Model || "",
            Capacity: d.Capacity ?? "",
            FuelType: d.FuelType || "",
            Ownership: d.Ownership || "",
            Engine_No: d.Engine_No || "",
            Chassis_No: d.Chassis_No || "",
            Area_Served: d.Area_Served || "",
            Status: d.Status || "Active",
            Remarks: d.Remarks || "",
            Is_Active: !!d.Is_Active,
          });
        } else {
          toast.error(vehicleRes.data.status_message || "Vehicle not found", {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
        if (assignRes.data.status_code === 1) {
          setAssignments(assignRes.data.ListofRecords || []);
        }
      })
      .catch(() =>
        toast.error("Network error", { position: toast.POSITION.TOP_RIGHT })
      )
      .finally(() => setLoading(false));
  }, [isEdit, vehicleId, post, auth]);

  const set = (field, value) => setV((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setSaving(true);
    post(
      "Vehicle/SaveVehicle",
      auth({
        ...v,
        Capacity: v.Capacity === "" ? null : parseInt(v.Capacity, 10),
      })
    )
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
          });
          navigate("/Transport/ManageVehicles");
        } else {
          // Duplicate registration lands here, which is the common case.
          toast.error(res.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 6000,
          });
        }
      })
      .catch(() =>
        toast.error("Network error", { position: toast.POSITION.TOP_RIGHT })
      )
      .finally(() => setSaving(false));
  };

  return (
    <>
      <TransportMenu />
      <ToastContainer />

      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-8">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/Dashboard">Dashboard</NavLink>
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/Transport/ManageVehicles">Vehicles</NavLink>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      {isEdit ? "Edit" : "Add"}
                    </li>
                  </ol>
                </nav>
                <div className="page-title">
                  {isEdit ? `Edit ${v.Registration_No}` : "Add Vehicle"}
                </div>
              </div>
              <div className="col-md-4 text-md-end">
                <NavLink
                  to="/Transport/ManageVehicles"
                  className="button button-white button-icon"
                >
                  Back to list
                  <i className="ri-arrow-left-line"></i>
                </NavLink>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="container">
            {loading ? (
              <div className="card">
                <div className="card-body text-center py-5">
                  <Spinner animation="border" role="status" />
                </div>
              </div>
            ) : (
              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <div className="card mb-3">
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label">
                          Registration No <span className="text-danger">*</span>
                        </label>
                        <Form.Control
                          type="text"
                          required
                          maxLength={50}
                          placeholder="e.g. MBJ-1005"
                          value={v.Registration_No}
                          onChange={(e) => set("Registration_No", e.target.value)}
                        />
                        <Form.Control.Feedback type="invalid">
                          Registration number is required.
                        </Form.Control.Feedback>
                        <div className="form-text">
                          Must be unique — this is how the bus is identified in the fuel
                          log and every report.
                        </div>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Model</label>
                        <Form.Control
                          type="text"
                          maxLength={100}
                          placeholder="e.g. Isuzu"
                          value={v.Model}
                          onChange={(e) => set("Model", e.target.value)}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Seating Capacity</label>
                        <Form.Control
                          type="number"
                          min={0}
                          value={v.Capacity}
                          onChange={(e) => set("Capacity", e.target.value)}
                        />
                      </div>

                      <div className="col-md-4">
                        <label className="form-label">Fuel Type</label>
                        <select
                          className="form-select"
                          value={v.FuelType}
                          onChange={(e) => set("FuelType", e.target.value)}
                        >
                          <option value="">-- select --</option>
                          <option value="Diesel">Diesel</option>
                          <option value="Petrol">Petrol</option>
                          <option value="CNG">CNG</option>
                          <option value="Hybrid">Hybrid</option>
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Ownership</label>
                        <select
                          className="form-select"
                          value={v.Ownership}
                          onChange={(e) => set("Ownership", e.target.value)}
                        >
                          <option value="">-- select --</option>
                          <option value="Private">Private (school owned)</option>
                          <option value="Contract">Contract / hired</option>
                          <option value="Leased">Leased</option>
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select"
                          value={v.Status}
                          onChange={(e) => set("Status", e.target.value)}
                        >
                          <option value="Active">Active</option>
                          <option value="Workshop">In workshop</option>
                          <option value="Standby">Standby</option>
                          <option value="Retired">Retired</option>
                        </select>
                      </div>

                      <div className="col-md-4">
                        <label className="form-label">Engine No</label>
                        <Form.Control
                          type="text"
                          maxLength={100}
                          value={v.Engine_No}
                          onChange={(e) => set("Engine_No", e.target.value)}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Chassis No</label>
                        <Form.Control
                          type="text"
                          maxLength={100}
                          value={v.Chassis_No}
                          onChange={(e) => set("Chassis_No", e.target.value)}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Area Served</label>
                        <Form.Control
                          type="text"
                          maxLength={200}
                          value={v.Area_Served}
                          onChange={(e) => set("Area_Served", e.target.value)}
                        />
                      </div>

                      <div className="col-md-8">
                        <label className="form-label">Remarks</label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          maxLength={500}
                          value={v.Remarks}
                          onChange={(e) => set("Remarks", e.target.value)}
                        />
                      </div>
                      <div className="col-md-4 d-flex align-items-end">
                        <Form.Check
                          type="checkbox"
                          id="isActive"
                          label="Active"
                          checked={v.Is_Active}
                          onChange={(e) => set("Is_Active", e.target.checked)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {isEdit && assignments.length > 0 ? (
                  <div className="card mb-3">
                    <div className="card-body">
                      <h6 className="mb-2">Driver history</h6>
                      <p className="small text-muted">
                        Assignments are dated, so it is always possible to say who was
                        driving this bus on a given day.
                      </p>
                      <div className="table-responsive">
                        <table className="table table-sm table-theme mb-0">
                          <thead>
                            <tr>
                              <th>Driver</th>
                              <th>Designation</th>
                              <th>Role</th>
                              <th>From</th>
                              <th>To</th>
                            </tr>
                          </thead>
                          <tbody>
                            {assignments.map((a) => (
                              <tr key={a.ID}>
                                <td>{a.StaffName}</td>
                                <td>{a.Designation}</td>
                                <td>{a.Role}</td>
                                <td>{moment(a.From_Date).format("DD/MM/YYYY")}</td>
                                <td>
                                  {a.To_Date ? (
                                    moment(a.To_Date).format("DD/MM/YYYY")
                                  ) : (
                                    <span className="badge bg-success">current</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="d-flex gap-2">
                  <button
                    type="submit"
                    className="button button-primary"
                    disabled={saving}
                  >
                    {saving ? "Saving…" : isEdit ? "Update Vehicle" : "Add Vehicle"}
                  </button>
                  <NavLink
                    to="/Transport/ManageVehicles"
                    className="button button-light"
                  >
                    Cancel
                  </NavLink>
                </div>
              </Form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default EditVehicle;
